import type { CSSProperties } from "react";
import { useSwarmData } from "../hooks/useSwarmData";
import { useRecentVerifications } from "../hooks/useRecentVerifications";
import { AgentCard } from "../components/AgentCard";
import { MetricsChart } from "../components/MetricsChart";
import { VerificationLog } from "../components/VerificationLog";
import { ErrorBanner } from "../components/ErrorBanner";
import { RecentFailuresPanel } from "../components/RecentFailuresPanel";
import { OverviewStats } from "../components/OverviewStats";
import { ConversionFunnelPanel } from "../components/ConversionFunnelPanel";

function formatCents(cents: number): string {
  return `€${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

interface KpiProps {
  label: string;
  value: string | number;
  caption: string;
  accent?: string;
  delay?: number;
}

function Kpi({ label, value, caption, accent, delay = 0 }: KpiProps) {
  return (
    <div
      className="panel panel-hover p-5 reveal relative overflow-hidden"
      style={{ animationDelay: `${delay}ms` } as CSSProperties}
    >
      <span className="absolute top-0 right-0 w-8 h-px" style={{ background: accent ?? "var(--color-border-strong)" }} />
      <p className="eyebrow">{label}</p>
      <p
        className="font-display italic text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] mt-2 tabular-nums"
        style={{ color: accent ?? "var(--color-text)" }}
      >
        {value}
      </p>
      <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] mt-2">
        {caption}
      </p>
    </div>
  );
}

export function Overview() {
  const { data, loading, error, refresh } = useSwarmData();
  // Live verification log — polls /api/verifications/recent every 10s
  // and overrides the snapshot from useSwarmData with fresher data.
  const recent = useRecentVerifications({ limit: 20 });

  if (error && !data) {
    return <ErrorBanner message={error} onRetry={refresh} context="swarm telemetry" />;
  }

  if (loading || !data) {
    return (
      <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
        <span className="live-dot text-[var(--color-accent)]" />
        <span className="font-mono text-xs tracking-[0.18em] uppercase blink">
          Loading swarm telemetry…
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-8 lg:space-y-10">
      {/* Page header */}
      <header className="reveal" style={{ animationDelay: "0ms" }}>
        <p className="eyebrow">Console · 01</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">Swarm </span>
          <span className="text-accent-gradient">Overview</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Live agent telemetry, dual-model verification log, and budget consumption across the autonomous operations layer.
        </p>
      </header>

      {/* KPI strip */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Active Agents"
          value={data.totals.activeAgents}
          caption={`of ${data.totals.totalAgents} provisioned`}
          accent="var(--color-status-active)"
          delay={60}
        />
        <Kpi
          label="Tasks Completed"
          value={data.totals.tasksCompleted.toLocaleString()}
          caption="across all agents"
          accent="var(--color-text)"
          delay={120}
        />
        <Kpi
          label="Verification Rate"
          value={`${data.totals.verificationPassRate}%`}
          caption="dual-model pass rate"
          accent="var(--color-accent-glow)"
          delay={180}
        />
        <Kpi
          label="Budget Used"
          value={formatCents(data.totals.totalBudgetUsedCents)}
          caption="this period"
          accent="var(--color-status-running)"
          delay={240}
        />
      </section>

      {/* Agent grid */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Agent Status</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.agents.map((agent, i) => (
            <div
              key={agent.id}
              className="reveal"
              style={{ animationDelay: `${300 + i * 60}ms` } as CSSProperties}
            >
              <AgentCard {...agent} />
            </div>
          ))}
        </div>
      </section>

      {/* Charts */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Telemetry · 14d</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="reveal" style={{ animationDelay: "640ms" }}>
            <MetricsChart
              title="Daily Tasks"
              data={data.dailyMetrics.map((d) => ({ date: d.date, value: d.tasks }))}
              accent="accent"
            />
          </div>
          <div className="reveal" style={{ animationDelay: "720ms" }}>
            <MetricsChart
              title="Daily Budget"
              data={data.dailyMetrics.map((d) => ({ date: d.date, value: d.budgetCents }))}
              accent="sky"
              formatValue={formatCents}
            />
          </div>
        </div>
      </section>

      {/* Verification log */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Verification Stream</p>
        </div>
        <div className="reveal" style={{ animationDelay: "820ms" }}>
          <VerificationLog
            entries={recent.entries.length > 0 ? recent.entries : data.verifications}
            subtitle={recent.error ? "offline" : "live · 10s"}
          />
        </div>
      </section>

      <OverviewStats />
      <ConversionFunnelPanel />
      <RecentFailuresPanel />
    </div>
  );
}
